import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import { Business, People, FolderOpen, Add } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { UserRole } from '../../types';

interface Organization {
  id: string;
  name: string;
  description?: string;
  stats: {
    totalUsers: number;
    activeUsers: number;
    totalProjects: number;
    activeProjects: number;
  };
}

const OrganizationSelector: React.FC = () => {
  const { user, selectedOrganizationId, setSelectedOrganization } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: '',
    website: ''
  });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (user?.role === UserRole.ROOT_USER) {
      loadOrganizations();
    }
  }, [user]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOrganizations();
      console.log('🏢 OrganizationSelector: Loaded organizations:', response);
      setOrganizations(response);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (organizationId: string) => {
    // Convert empty string to null for "View All Organizations"
    const orgId = organizationId || null;
    console.log('🏢 OrganizationSelector: Changing organization to:', orgId);
    setSelectedOrganization(orgId);
  };

  const handleViewAll = () => {
    setSelectedOrganization(null);
  };

  const handleCreateOrganization = async () => {
    try {
      setCreating(true);
      setCreateError('');
      
      const newOrg = await apiService.createOrganization(createFormData);
      // Add stats to the new organization to match the expected structure
      const newOrgWithStats = {
        ...newOrg,
        stats: {
          totalUsers: 0,
          activeUsers: 0,
          totalProjects: 0,
          activeProjects: 0
        }
      };
      setOrganizations(prev => [...prev, newOrgWithStats]);
      setCreateDialogOpen(false);
      setCreateFormData({ name: '', description: '', website: '' });
    } catch (error: any) {
      setCreateError(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setCreateError('');
    setCreateFormData({ name: '', description: '', website: '' });
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateError('');
    setCreateFormData({ name: '', description: '', website: '' });
  };

  if (user?.role !== UserRole.ROOT_USER) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          🏢 Organization Selection
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
          size="small"
          sx={{
            borderColor: '#64748b',
            color: '#334155',
            '&:hover': {
              borderColor: '#334155',
              backgroundColor: 'rgba(51, 65, 85, 0.04)',
            }
          }}
        >
          Create Organization
        </Button>
      </Box>
      
      {!selectedOrganizationId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            🔒 Privacy Protection Active
          </Typography>
          <Typography variant="body2">
            You must select an organization to view data. Cross-organization data access is disabled for privacy and compliance.
          </Typography>
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 2 }} required>
        <InputLabel>Select Organization *</InputLabel>
        <Select
          value={selectedOrganizationId || ''}
          onChange={(e) => handleOrganizationChange(e.target.value)}
          label="Select Organization *"
          displayEmpty={false}
        >
          {organizations.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business />
                {org.name}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedOrganizationId && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Selected Organization
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                    <Typography variant="h6">
                      {organizations.find(org => org.id === selectedOrganizationId)?.stats?.totalUsers || 0}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People color="success" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Users
                    </Typography>
                    <Typography variant="h6">
                      {organizations.find(org => org.id === selectedOrganizationId)?.stats?.activeUsers || 0}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderOpen color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Projects
                    </Typography>
                    <Typography variant="h6">
                      {organizations.find(org => org.id === selectedOrganizationId)?.stats?.totalProjects || 0}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderOpen color="success" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Projects
                    </Typography>
                    <Typography variant="h6">
                      {organizations.find(org => org.id === selectedOrganizationId)?.stats?.activeProjects || 0}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {!selectedOrganizationId && organizations.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              All Organizations Overview
            </Typography>
            <Grid container spacing={2}>
              {organizations.map((org) => (
                <Grid item xs={12} sm={6} md={4} key={org.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {org.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          icon={<People />}
                          label={`${org.stats?.activeUsers || 0}/${org.stats?.totalUsers || 0} Users`}
                          size="small"
                          color="primary"
                        />
                        <Chip
                          icon={<FolderOpen />}
                          label={`${org.stats?.activeProjects || 0}/${org.stats?.totalProjects || 0} Projects`}
                          size="small"
                          color="secondary"
                        />
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOrganizationChange(org.id)}
                        fullWidth
                        sx={{
                          borderColor: '#64748b',
                          color: '#334155',
                          '&:hover': {
                            borderColor: '#334155',
                            backgroundColor: 'rgba(51, 65, 85, 0.04)',
                          }
                        }}
                      >
                        Select Organization
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            variant="outlined"
            value={createFormData.name}
            onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={createFormData.description}
            onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Website"
            type="url"
            fullWidth
            variant="outlined"
            value={createFormData.website}
            onChange={(e) => setCreateFormData(prev => ({ ...prev, website: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseCreateDialog} 
            disabled={creating}
            sx={{
              color: '#64748b',
              '&:hover': {
                backgroundColor: 'rgba(100, 116, 139, 0.04)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateOrganization} 
            variant="outlined"
            disabled={creating || !createFormData.name.trim()}
            sx={{
              borderColor: '#64748b',
              color: '#334155',
              '&:hover': {
                borderColor: '#334155',
                backgroundColor: 'rgba(51, 65, 85, 0.04)',
              },
              '&:disabled': {
                borderColor: '#cbd5e1',
                color: '#94a3b8',
              }
            }}
          >
            {creating ? 'Creating...' : 'Create Organization'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationSelector;
